import type { BattleSummary } from "@loot-os/api-contract";
import { capitalizeTerm } from "@loot-os/api-contract";
import { buttonVariants } from "@loot-os/ui/components/button";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { BattleCard } from "../components/battles/BattleCard.js";
import type { RoundBox } from "../components/battles/BattleRoundsStrip.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { battleVisualStatus } from "../lib/battleTimeline.js";
import {
  battlesQueryOptions,
  boxesQueryOptions,
  myBattlesInfiniteQueryOptions,
} from "../lib/queries.js";
import { useShopper } from "../lib/shopper.js";
import { useTerminology } from "../lib/terminology.js";
import { useLobbyChannel } from "../lib/useBattleChannel.js";

const INITIAL_VISIBLE = 12;
const REVEAL_STEP = 12;

/**
 * GET /v1/battles — the lobby: joinable first, then in progress, then finished (faded). A
 * signed-in shopper also sees their own private battles here, badged; everyone else sees the
 * public ones alone. The lobby is fetched anew once the session token exists (the key changes).
 */
export function BattlesPage() {
  const { user, sessionToken } = useShopper();
  const { box, item, battle } = useTerminology();
  const live = useLobbyChannel();
  const [tab, setTab] = useState<"all" | "mine">("all");
  const [visible, setVisible] = useState(INITIAL_VISIBLE);

  const lobby = useQuery(
    battlesQueryOptions({ viewer: sessionToken && user ? user.id : "anon", poll: !live }),
  );
  const mine = useInfiniteQuery({
    ...myBattlesInfiniteQueryOptions(),
    enabled: tab === "mine" && !!sessionToken,
  });
  const boxes = useQuery(boxesQueryOptions());
  const boxMap = useMemo(() => {
    const map = new Map<string, RoundBox>();
    for (const box of boxes.data ?? []) map.set(box.id, { name: box.name, imageUrl: box.imageUrl });
    return map;
  }, [boxes.data]);

  const error = lobby.error
    ? lobby.error instanceof Error
      ? lobby.error.message
      : `failed to load ${battle.plural}`
    : null;

  const battles: BattleSummary[] =
    tab === "mine"
      ? (mine.data?.pages.flatMap((page) => page.data) ?? [])
      : (lobby.data?.data ?? []);
  const groups = useMemo(() => {
    const joinable = battles.filter((b) => battleVisualStatus(b) === "joinable");
    const inProgress = battles.filter((b) => battleVisualStatus(b) === "in-progress");
    const finished = battles.filter((b) => battleVisualStatus(b) === "finished");
    return [
      { key: "joinable", title: "Open lobbies", battles: joinable },
      { key: "in-progress", title: "In progress", battles: inProgress },
      { key: "finished", title: "Finished", battles: finished },
    ];
  }, [battles]);
  let budget = visible;

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{capitalizeTerm(battle.plural)}</h1>
          <p className="text-sm text-muted-foreground">
            Same {box.plural}, same rounds, one winner takes every {item.singular}. Every roll is
            provably fair.
          </p>
        </div>
        {user ? (
          <Link to="/battles/create" className={buttonVariants()}>
            Create {battle.singular}
          </Link>
        ) : (
          <Link to="/login" className={buttonVariants({ variant: "secondary" })}>
            Sign in to create {battle.plural}
          </Link>
        )}
      </div>

      <Tabs value={tab} onValueChange={(value) => setTab(value === "mine" ? "mine" : "all")}>
        <TabsList>
          <TabsTrigger value="all">All {battle.plural}</TabsTrigger>
          <TabsTrigger value="mine" disabled={!user}>
            My {battle.plural}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {lobby.isPending && tab === "all" ? (
        <div className="grid gap-3">
          {["a", "b", "c"].map((id) => (
            <Skeleton key={id} className="h-32 rounded-xl" />
          ))}
        </div>
      ) : battles.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {tab === "mine"
            ? `You haven't joined any ${battle.plural} yet.`
            : `No ${battle.plural} yet — create one and invite a friend, or start against bots.`}
        </p>
      ) : (
        <div className="grid gap-6">
          {groups
            .filter((group) => group.battles.length > 0)
            .map((group) => {
              const shown = group.battles.slice(0, Math.max(0, budget));
              budget -= shown.length;
              if (shown.length === 0) return null;
              return (
                <section key={group.key} className="grid gap-3">
                  <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {group.title}
                  </h2>
                  {shown.map((battle) => (
                    <BattleCard
                      key={battle.id}
                      battle={battle}
                      boxes={boxMap}
                      viewerEndUserId={lobbyViewerId(battles, user?.id)}
                    />
                  ))}
                </section>
              );
            })}
          {battles.length > visible ? (
            <button
              type="button"
              className={buttonVariants({ variant: "secondary" })}
              onClick={() => setVisible((v) => v + REVEAL_STEP)}
            >
              See more
            </button>
          ) : null}
          {tab === "mine" && mine.hasNextPage ? (
            <button
              type="button"
              className={buttonVariants({ variant: "secondary" })}
              disabled={mine.isFetchingNextPage}
              onClick={() => void mine.fetchNextPage()}
            >
              Load more
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * The shopper's Loot end-user id is not on the BFF session; the summaries carry end-user ids,
 * and the `me` block is only on the detail. The lobby therefore cannot name "You" and shows the
 * display name instead; passing undefined keeps `SeatName` honest.
 */
function lobbyViewerId(_battles: BattleSummary[], _shopperId: string | undefined): undefined {
  return undefined;
}
